import { useCallback, useEffect, useRef } from 'react';
import { useDsTranslate, useDsNavigate } from '../../context/DsProvider';
import { ForbiddenIllustration } from './ForbiddenIllustration';
import styles from './Forbidden.module.scss';

export type ForbiddenVariant = 'page' | 'inline' | 'empty-state';

/**
 * CTA precedence (from highest to lowest):
 *   1. `hideAction` = true  → no button rendered
 *   2. `onAction` provided  → renders <button>, calls onAction on click
 *   3. `actionHref` provided (and no onAction) → renders <a href=...>
 *   4. fallback             → renders <button> that calls `useDsNavigate('/')`
 *
 * If both `onAction` and `actionHref` are passed, `onAction` wins and the
 * rendered element stays a <button>.
 */
export interface ForbiddenProps {
  variant?: ForbiddenVariant;
  title?: string;
  body?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionHref?: string;
  hideAction?: boolean;
  className?: string;
}

function getIllustrationSize(variant: ForbiddenVariant): number {
  switch (variant) {
    case 'page': return 180;
    case 'inline': return 112;
    case 'empty-state': return 64;
  }
}

function getVariantClass(variant: ForbiddenVariant): string | undefined {
  switch (variant) {
    case 'page': return styles.page;
    case 'inline': return styles.inline;
    case 'empty-state': return styles.emptyState;
  }
}

export function Forbidden({
  variant = 'page',
  title,
  body,
  actionLabel,
  onAction,
  actionHref,
  hideAction = false,
  className,
}: ForbiddenProps) {
  const t = useDsTranslate();
  const dsNavigate = useDsNavigate();
  const actionRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null);

  // Stable ref callback so re-renders don't detach+re-attach the action ref
  // every render (which would race with the focus rAF below).
  const setActionRef = useCallback((el: HTMLAnchorElement | HTMLButtonElement | null) => {
    actionRef.current = el;
  }, []);

  // Focus the CTA on mount so keyboard users can recover with one Enter press.
  // Skipped on empty-state — small/nested contexts shouldn't yank focus.
  // The rAF is cancelled on unmount / dep change so the callback can't fire
  // into a stale ref (React 19 strict-mode double-mount + rapid variant flips).
  useEffect(() => {
    if (variant === 'empty-state' || hideAction) return;
    const id = requestAnimationFrame(() => actionRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [variant, hideAction]);

  const resolvedTitle = title ?? t('K_FORBIDDEN_TITLE', 'Access denied');
  const resolvedBody =
    body ?? t('K_FORBIDDEN_BODY', "You don't have permission to view this content. If you think this is a mistake, contact your administrator.");
  const resolvedActionLabel = actionLabel ?? t('K_FORBIDDEN_ACTION_HOME', 'Go to home');

  const handleClick = (e: React.MouseEvent) => {
    if (onAction) {
      e.preventDefault();
      onAction();
      return;
    }
    if (!actionHref) {
      e.preventDefault();
      dsNavigate('/');
    }
  };

  // empty-state avoids a heading element (lives inside table cells) — <strong> for SR emphasis.
  const isEmptyState = variant === 'empty-state';
  const Heading = variant === 'page' ? 'h1' : variant === 'inline' ? 'h2' : 'p';
  const actionClass = variant === 'page' ? styles.primaryAction : styles.linkAction;

  const rootClass = [styles.root, getVariantClass(variant), className].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <ForbiddenIllustration size={getIllustrationSize(variant)} className={styles.illustration} />
      <Heading className={styles.title}>
        {isEmptyState ? <strong>{resolvedTitle}</strong> : resolvedTitle}
      </Heading>
      <p className={styles.body}>{resolvedBody}</p>
      {!hideAction && (
        actionHref && !onAction ? (
          <a
            ref={setActionRef}
            href={actionHref}
            className={`${styles.action} ${actionClass}`}
          >
            {resolvedActionLabel}
          </a>
        ) : (
          <button
            ref={setActionRef}
            type="button"
            onClick={handleClick}
            className={`${styles.action} ${actionClass}`}
          >
            {resolvedActionLabel}
          </button>
        )
      )}
    </div>
  );
}
